import { executeGraphQlQuery } from './storefront-graphql-helper'
import { GQLVariant, Product, Response, ResponseSearch, ShopifyProduct, ShopifyProductData, SwatchProduct, Variant } from '@/components/libs/algolia-service/types'

export const shouldConvertCartCurrency = (): boolean => {
  const path = window.Shopify?.routes?.root || window.location.pathname.toLowerCase()
  const auStoreUrl = window.GM_STATE?.shopify?.auStoreUrl || ''
  const caStoreUrl = window.GM_STATE?.shopify?.caStoreUrl || ''
  const ukStoreUrl = window.GM_STATE?.shopify?.ukStoreUrl || ''

  const isOnAUStore = window.location.origin === auStoreUrl

  // Check if we have an active market subfolder (CA or GB)
  const hasActiveMarket = path.startsWith(caStoreUrl) || path.startsWith(ukStoreUrl)

  // Only convert if we're on AU store AND have an active market
  if (isOnAUStore && hasActiveMarket) {
    return true
  }

  return false
}

// Type guards to check input types
const isResponseSearch = (input: any): input is ResponseSearch => {
  return input && typeof input === 'object' && 'items' in input && Array.isArray(input.items)
}

const isResponse = (input: any): input is Response => {
  return input && typeof input === 'object' && 'products' in input && Array.isArray(input.products)
}

const isProductArray = (input: any): input is Product[] => {
  return Array.isArray(input) && input.length > 0 && 'id' in input[0] && 'price' in input[0]
}

const isSwatchProductArray = (input: any): input is SwatchProduct[] => {
  return Array.isArray(input) && input.length > 0 && 'id' in input[0] && 'price' in input[0] && 'shipmentDate' in input[0]
}

// Helper function to extract products from different input types
const extractProducts = (input: Response | ResponseSearch | Product[] | SwatchProduct[]): Product[] | SwatchProduct[] => {
  if (isResponseSearch(input)) {
    return input.items
  }

  if (isResponse(input)) {
    return input.products
  }

  if (isProductArray(input)) {
    return input
  }

  if (isSwatchProductArray(input)) {
    return input
  }

  throw new Error('Invalid input type for currency conversion')
}

export const getProductPricesWithCurrency = async (input: Response | ResponseSearch | Product[]): Promise<Response | ResponseSearch | Product[]> => {
  const country = window.Shopify.country

  if (country === 'US' || country === 'AU') {
    return input
  }

  // Extract products using type guard
  const products = extractProducts(input)

  if (products.length === 0) {
    return input
  }

  const query = `
    query ($ids: [ID!]!, $country: CountryCode!)
      @inContext(country: $country) {
      nodes(ids: $ids) {
        ... on Product {
          id
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 25) {
            nodes {
              id
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  `

  const productIds: string[] = products.map(product => `gid://shopify/Product/${product.id}`)

  const variables = {
    ids: productIds,
    country: country.toUpperCase()
  }

  const response = await executeGraphQlQuery({ query, variables })
    .then((res: any) => res.json())
    .then((data: any) => {
      if (data.errors) {
        throw data
      }

      return data.data
    })
    .catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.warn('Error fetching product prices with currency', err)

      return input
    })

  const productPrices: ShopifyProductData | null = response.nodes ? response : null

  // Update product prices with the current currency
  products.forEach((product: Product | SwatchProduct) => {
    productPrices?.nodes.forEach((updatedPrice: ShopifyProduct | null) => {
      const id = updatedPrice?.id.split('/')
      const productId = id?.[id.length - 1]

      if (productId === product.id.toString()) {
        const minPrice = Number(updatedPrice?.priceRange.minVariantPrice.amount)
        const maxPrice = Number(updatedPrice?.priceRange.maxVariantPrice.amount)

        product.price = !isNaN(minPrice) ? minPrice * 100 : 0

        // Algolia will sometimes send 0 back here and if we put in a compare at price
        // When the original price was 0, it will cause a greyed out price on the product card
        if (product.compareAtPrice !== 0) {
          product.compareAtPrice = !isNaN(maxPrice) ? maxPrice * 100 : 0
        }

        product.variants.forEach((variant: Variant) => {
          updatedPrice?.variants.nodes.forEach((gqlVariant: GQLVariant) => {
            const gqlIdParts = gqlVariant.id.split('/')
            const gqlVariantId = gqlIdParts[gqlIdParts.length - 1]

            if (gqlVariantId === variant.id.toString()) {
              const variantPrice = Number(gqlVariant.price.amount)
              const variantCompareAtPrice = Number(gqlVariant.compareAtPrice?.amount)

              variant.price = !isNaN(variantPrice) ? variantPrice * 100 : 0
              variant.compareAtPrice = !isNaN(variantCompareAtPrice) ? variantCompareAtPrice * 100 : 0
            }
          })
        })
      }
    })
  })

  // Return the same type as input with updated products
  if (isResponseSearch(input)) {
    return { ...input, items: products as Product[] }
  }

  if (isResponse(input)) {
    return { ...input, products: products as Product[] }
  }

  if (isProductArray(input)) {
    return products as Product[]
  }

  if (isSwatchProductArray(input)) {
    return input
  }

  return input
}
